"use client"

import { cn } from "@/lib/utils"

/* ds-typo-ignore-file — reading chip styled after the membership lab-upload
   overlay (app/(frontend)/[country]/[locale]/_components/included-grid.tsx →
   LabUploadOverlay) + the report findings-severity dots (report-mockup-cards):
   translucent dark glass + white text. Intentional sub-DS px sizes. */

export type ReadingChart = {
  /* vertical position of the "you" marker, 0 = top (high), 100 = bottom (low) */
  youTopPct: number
  youTone: "green" | "sand" | "terra"
  cohortTopPct: number
  cohortLeftPct: number
}

/* A findings-count badge like the report's summary pills — "• 2 Observation". */
export type Finding = {
  count: number
  label: string
  tone: "green" | "olive"
}

/* Compact vertical range bar (terracotta → sand → light-green) with a cohort
   dot and the "you" marker — mirrors the membership overlay's mini range. */
function VerticalRangeBar({
  youTopPct,
  youTone,
  cohortTopPct,
  cohortLeftPct,
}: ReadingChart) {
  return (
    <div className="relative h-[40px] w-[52px] shrink-0">
      <div className="absolute top-0 right-0 flex h-full w-[3px] flex-col gap-[1.5px]">
        <div className="bg-terracotta-300 flex-1 rounded-t-[2px]" />
        <div className="bg-sand-400 flex-1" />
        <div className="bg-light-green-400 flex-1 rounded-b-[2px]" />
      </div>
      <div
        className="absolute size-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/70"
        style={{ top: `${cohortTopPct}%`, left: `${cohortLeftPct}%` }}
      />
      <div
        className={cn(
          "absolute size-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full shadow-[0_0_0_1.25px_rgba(255,255,255,0.95)]",
          youTone === "green"
            ? "bg-light-green-400"
            : youTone === "sand"
              ? "bg-sand-400"
              : "bg-terracotta-300"
        )}
        style={{ top: `${youTopPct}%`, left: "72%" }}
      />
    </div>
  )
}

/* Findings-count badges like the report's summary pills — a coloured dot +
   count + severity label, on a faint outlined pill. */
function FindingBadges({ findings }: { findings: Finding[] }) {
  return (
    <div className="flex flex-wrap items-center gap-[6px]">
      {findings.map((f, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-[4px] rounded-full border border-white/20 px-[7px] py-[2px] text-[10px] leading-[13px] text-white"
        >
          <span
            className={cn(
              "size-[5px] rounded-full",
              f.tone === "green" ? "bg-deep-green-500" : "bg-light-green-400"
            )}
          />
          <span className="font-medium">{f.count}</span>
          <span className="font-normal text-white/85">{f.label}</span>
        </span>
      ))}
    </div>
  )
}

/* Glass reading chip for a metabolism MRI card — title on top, then either a
   measured reading (value + status pill + vertical range bar) or, for a
   findings card like the organs, a severity-dot gauge + label. Translucent dark
   glass + white text. */
export function MetabolismReadingChip({
  title,
  value,
  unit,
  statusLabel,
  chart,
  findings,
}: {
  title: string
  value?: string
  unit?: string
  statusLabel: string
  chart?: ReadingChart
  findings?: Finding[]
}) {
  return (
    <div className="pointer-events-none flex min-h-[60px] items-center justify-between gap-[10px] rounded-[14px] bg-white/[0.1] px-[12px] py-[10px] backdrop-blur-[6px]">
      <div className="flex min-w-0 flex-col gap-[3px]">
        <p className="text-[13px] leading-[16px] font-semibold tracking-[-0.2px] text-white">
          {title}
        </p>
        {findings ? (
          <div className="mt-[4px]">
            <FindingBadges findings={findings} />
          </div>
        ) : (
          <div className="mt-[3px] flex items-center gap-[8px]">
            {value ? (
              <div className="flex items-baseline gap-1">
                <span className="text-[18px] leading-none font-medium text-white">
                  {value}
                </span>
                {unit ? (
                  <span className="pb-[1px] font-mono text-[9px] leading-3 font-normal tracking-wider text-white/70">
                    {unit}
                  </span>
                ) : null}
              </div>
            ) : null}
            <span
              role="status"
              className="w-fit truncate rounded-full bg-white/20 px-2 py-0.5 text-[10px] leading-[12px] font-medium text-nowrap text-white/90"
            >
              {statusLabel}
            </span>
          </div>
        )}
      </div>
      {chart ? <VerticalRangeBar {...chart} /> : null}
    </div>
  )
}
